import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useRef } from "react";
import { Seo } from "../components/onix/Seo";
import { ShieldMark } from "../components/onix/ShieldMark";
import { SectionEyebrow } from "../components/onix/SectionEyebrow";
import { ProductCard } from "../components/onix/ProductCard";
import { Reveal } from "../components/onix/Reveal";
import { Marquee } from "../components/onix/Marquee";
import { CTASection } from "../components/onix/CTASection";
import { CinematicScene, useIsMobile } from "../components/onix/CinematicScene";
import { getTools, getPillars } from "../data/tools";
import { useLang } from "../i18n/LanguageContext";

const SCENES = {
  hero: "/assets/img/onix_scene_1.webp",
  heroM: "/assets/img/onix_scene_1_m.webp",
  system: "/assets/img/onix_scene_2.webp",
  systemM: "/assets/img/onix_scene_2_m.webp",
  tools: "/assets/img/onix_scene_3.webp",
  toolsM: "/assets/img/onix_scene_3_m.webp",
  trust: "/assets/img/onix_scene_4.webp",
  trustM: "/assets/img/onix_scene_4_m.webp",
  cta: "/assets/img/onix_scene_5.webp",
  ctaM: "/assets/img/onix_scene_5_m.webp",
};

const HeroLine = ({ text, delay, reduce }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={reduce ? false : { y: "108%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.2, 0.8, 0.2, 1] }}
    >
      {text}
    </motion.span>
  </span>
);

export default function Home() {
  const reduce = useReducedMotion();
  const isMobile = useIsMobile();
  const { lang, t } = useLang();
  const th = t.home;
  const tools = getTools(lang);
  const pillars = getPillars(lang);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"], layoutEffect: false });
  const shieldY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 90]);
  const shieldOpacity = useTransform(scrollYProgress, [0, 0.9], [1, 0.35]);
  const heroImgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const heroImgY = useTransform(scrollYProgress, [0, 1], ["0%", "6%"]);
  const heroImgOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);
  const heroAnimated = !reduce && !isMobile;

  return (
    <main className="pt-[76px]">
      <Seo title={th.seoTitle} description={th.seoDesc} path="/" />

      {/* HERO — ESCENA 1 */}
      <section ref={heroRef} data-testid="hero-institutional" className="relative border-b border-onix-line overflow-hidden">
        <motion.img
          src={isMobile ? SCENES.heroM : SCENES.hero}
          alt=""
          aria-hidden="true"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          style={heroAnimated ? { scale: heroImgScale, y: heroImgY, opacity: heroImgOpacity } : { opacity: 0.9 }}
        />
        <div className="scene-overlay absolute inset-0 pointer-events-none" aria-hidden="true" />
        <div className="hero-grid-lines" aria-hidden="true" />
        <div className="onix-shell relative grid lg:grid-cols-12 gap-10 items-center py-16 md:py-24 lg:min-h-[calc(100vh-76px)]">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <motion.p
              className="eyebrow mb-8"
              initial={reduce ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              ONIX · INTELLIGENCE & CYBERSECURITY SYSTEMS
            </motion.p>
            <h1 className="text-[2.1rem] leading-[1.08] sm:text-5xl lg:text-6xl font-bold font-serif sm:leading-[1.04] text-onix-white mb-8">
              {th.h1Lines.map((line, i) => (
                <HeroLine key={`${lang}-${i}`} text={line} delay={0.25 + i * 0.14} reduce={reduce} />
              ))}
            </h1>
            <motion.p
              className="text-base md:text-lg leading-relaxed text-onix-silver max-w-xl mb-10"
              initial={reduce ? false : { opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.75, ease: [0.2, 0.8, 0.2, 1] }}
            >
              {th.heroText}
            </motion.p>
            <motion.div
              className="flex flex-wrap gap-4"
              initial={reduce ? false : { opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
            >
              <Link data-testid="hero-cta-primary" to="/contacto" className="btn-onix-primary">
                {t.common.solicitarDemo}
                <ArrowRight size={14} aria-hidden="true" />
              </Link>
              <Link data-testid="hero-cta-secondary" to="/ecosistema" className="btn-onix-secondary">
                {th.ctaSecondary}
              </Link>
            </motion.div>
          </div>
          <div className="lg:col-span-5 order-1 lg:order-2 flex justify-center lg:justify-end">
            <motion.div
              style={{ y: shieldY, opacity: shieldOpacity }}
              initial={reduce ? false : { opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, delay: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
            >
              <ShieldMark
                variant="white"
                className="w-[260px] sm:w-[300px] lg:w-[clamp(430px,36vw,540px)]"
                eager
                alt="Escudo ONIX — Intelligence and Cybersecurity Systems"
              />
            </motion.div>
          </div>
        </div>
        <div className="onix-shell relative pb-10">
          <div className="hairline mb-6" aria-hidden="true" />
          <div className="flex flex-wrap items-center justify-between gap-4">
            <p data-testid="hero-motto" className="font-sans font-bold text-[11px] md:text-xs tracking-[0.28em] uppercase text-onix-white">
              INTELLIGENCE DRIVEN · MISSION FOCUSED
            </p>
            <p className="tech-label">{th.systemTag}</p>
          </div>
        </div>
      </section>

      {/* POSICIONAMIENTO / PILARES — ESCENA 2 */}
      <CinematicScene img={SCENES.system} imgMobile={SCENES.systemM} data-testid="pillars-section">
        <div className="onix-shell py-24 md:py-36">
          <Reveal>
            <SectionEyebrow index="01" label={th.s1Label} />
          </Reveal>
          <div className="grid lg:grid-cols-12 gap-10 mb-16">
            <Reveal className="lg:col-span-6">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-onix-white">
                {th.s1Title}
              </h2>
            </Reveal>
            <Reveal className="lg:col-span-6" delay={0.1}>
              <p className="text-base leading-relaxed text-onix-silver">
                {th.s1Text}
              </p>
            </Reveal>
          </div>
          <div className="border-b border-onix-line">
            {pillars.map((p, i) => (
              <Reveal key={p.num} delay={Math.min(i * 0.05, 0.3)}>
                <div
                  data-testid={`pillar-${p.num}`}
                  className="group grid md:grid-cols-12 gap-2 md:gap-8 items-baseline border-t border-onix-line py-6 md:py-7 transition-colors duration-300 hover:bg-onix-black2"
                >
                  <span className="tech-label md:col-span-1">{p.num}</span>
                  <h3 className="md:col-span-4 font-serif font-bold text-xl md:text-2xl text-onix-white transition-transform duration-300 group-hover:translate-x-2">
                    {p.name}
                  </h3>
                  <p className="md:col-span-7 text-sm leading-relaxed text-onix-silver">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </CinematicScene>

      <Marquee />

      {/* HERRAMIENTAS — ESCENA 3 */}
      <CinematicScene img={SCENES.tools} imgMobile={SCENES.toolsM} data-testid="tools-section">
        <div className="onix-shell py-24 md:py-36">
          <Reveal>
            <SectionEyebrow index="02" label={th.s2Label} />
          </Reveal>
          <Reveal>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-onix-white mb-16 max-w-2xl">
              {th.s2Title}
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5">
            {tools.map((tool, i) => {
              const spans = ["lg:col-span-6", "lg:col-span-6", "lg:col-span-4", "lg:col-span-4", "lg:col-span-4", "lg:col-span-6", "lg:col-span-6"];
              return (
                <Reveal key={tool.slug} className={`${spans[i]} md:col-span-1 flex`} delay={Math.min(i * 0.06, 0.3)}>
                  <ProductCard tool={tool} className="w-full" />
                </Reveal>
              );
            })}
          </div>
        </div>
      </CinematicScene>

      {/* CONFIANZA — ESCENA 4 */}
      <CinematicScene img={SCENES.trust} imgMobile={SCENES.trustM} data-testid="trust-section" className="border-t border-onix-line">
        <div className="onix-shell py-24 md:py-36 grid lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-5">
            <SectionEyebrow index="03" label={th.s3Label} />
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-onix-white">
              {th.s3Title}
            </h2>
          </Reveal>
          <Reveal className="lg:col-span-7" delay={0.1}>
            <p className="text-base leading-relaxed text-onix-silver mb-10 lg:pt-16">
              {th.s3Text}
            </p>
            <Link
              data-testid="trust-link-governance"
              to="/seguridad-y-gobierno"
              className="inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.14em] text-onix-white group"
            >
              <span className="border-b border-onix-line pb-1 transition-colors group-hover:border-onix-blue">
                {th.s3Link}
              </span>
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1.5" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </CinematicScene>

      {/* CTA — ESCENA 5 */}
      <CinematicScene img={SCENES.cta} imgMobile={SCENES.ctaM}>
        <CTASection
          index="04"
          label={th.ctaLabel}
          title={th.ctaTitle}
          text={th.ctaText}
          buttonLabel={t.common.hablarConOnix}
          to="/contacto"
        />
      </CinematicScene>
    </main>
  );
}
